/**
 * Slide Out Filters Widget JavaScript
 *
 * Handles interactions for the slide-out filters panel:
 * - Toggle button click
 * - Overlay click to close
 * - Collapsible filter groups
 * - Filter search
 * - AJAX filtering
 * - Apply/Reset buttons
 *
 * @package Storedash
 * @since 3.4.0
 */

(function() {
    'use strict';

    /**
     * SlideOutFilters Controller
     */
    class SlideOutFilters {
        constructor(wrapper) {
            this.wrapper = wrapper;
            this.widgetId = wrapper.getAttribute('data-widget-id') || 'unknown';
            this.settings = this._parseSettings();

            // Cache DOM elements (some may not exist depending on settings)
            this.toggle = wrapper.querySelector('.storedash-slide-out-toggle');
            this.panel = wrapper.querySelector('.storedash-slide-out-panel');
            this.overlay = wrapper.querySelector('.storedash-slide-out-overlay');
            this.closeBtn = wrapper.querySelector('.panel-close');
            this.applyBtn = wrapper.querySelector('.filter-apply');
            this.resetBtn = wrapper.querySelector('.filter-reset');
            this.searchInput = wrapper.querySelector('.filter-search-input');
            this.filterCheckboxes = wrapper.querySelectorAll('.filter-checkbox');
            this.groupToggles = wrapper.querySelectorAll('.filter-group-toggle');

            // Validate required elements
            if (!this.toggle) {
                return;
            }

            if (!this.panel) {
                return;
            }

            // Only bind events if required elements exist
            this._bindEvents();

            // Set initial checked states based on URL params
            this._syncWithURL();

            // BULLETPROOF FIX: Sync with other filter widgets
            this._bindCrossWidgetSync();
        }

        /**
         * BULLETPROOF FIX: Sync this slide-out with regular filter widgets
         * When any filter changes, update checkboxes in this panel
         */
        _bindCrossWidgetSync() {
            document.addEventListener('storedash:filters:changed', (event) => {
                if (event.detail.filters) {
                    this._syncWithFilters(event.detail.filters);
                }
            });
        }

        /**
         * Sync checkboxes with provided filter state
         */
        _syncWithFilters(filters) {
            this.filterCheckboxes.forEach(checkbox => {
                const filterGroup = checkbox.closest('.filter-group');
                if (!filterGroup) return;

                const sourceName = filterGroup.getAttribute('data-source-name');
                const value = checkbox.getAttribute('data-value');

                // Check/uncheck based on filter state
                checkbox.checked = filters[sourceName]?.includes(value) || false;
            });
        }

        /**
         * Parse settings from data attribute
         */
        _parseSettings() {
            const settingsEl = this.wrapper.getAttribute('data-settings');
            try {
                return settingsEl ? JSON.parse(settingsEl) : {};
            } catch (e) {
                return {};
            }
        }

        /**
         * Bind all event listeners
         */
        _bindEvents() {
            // Toggle button
            if (this.toggle) {
                this.toggle.addEventListener('click', () => this._togglePanel());
            }

            // Overlay
            if (this.overlay && this.settings.close_on_overlay_click) {
                this.overlay.addEventListener('click', () => this._closePanel());
            }

            // Close button
            if (this.closeBtn) {
                this.closeBtn.addEventListener('click', () => this._closePanel());
            }

            // Filter checkboxes
            this.filterCheckboxes.forEach(checkbox => {
                checkbox.addEventListener('change', () => {
                    if (this.settings.instant_apply) {
                        this._applyFilters();
                    }
                });
            });

            // Apply button
            if (this.applyBtn) {
                this.applyBtn.addEventListener('click', () => this._applyFilters());
            }

            // Reset button
            if (this.resetBtn) {
                this.resetBtn.addEventListener('click', () => this._resetFilters());
            }

            // Filter search
            if (this.searchInput) {
                this.searchInput.addEventListener('input', (e) => this._searchFilters(e.target.value));
            }

            // Collapsible groups
            this.groupToggles.forEach(toggle => {
                toggle.addEventListener('click', (e) => this._toggleGroup(e));
            });

            // Close on Escape key
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this._isPanelOpen()) {
                    this._closePanel();
                }
            });
        }

        /**
         * Toggle panel visibility
         */
        _togglePanel() {
            if (this._isPanelOpen()) {
                this._closePanel();
            } else {
                this._openPanel();
            }
        }

        /**
         * Open panel
         */
        _openPanel() {
            if (!this.panel) return;

            // Show panel and overlay
            this.panel.style.display = 'block';
            if (this.overlay) {
                this.overlay.style.display = 'block';
            }

            // Trigger animation (small delay for smooth animation)
            setTimeout(() => {
                this.panel.classList.add('open');
                if (this.overlay) {
                    this.overlay.classList.add('visible');
                }
            }, 10);

            // Update button state
            this.toggle.setAttribute('aria-expanded', 'true');

            // Prevent body scroll on mobile
            document.body.style.overflow = 'hidden';

            // Focus management
            this.closeBtn?.focus();

            this._focusTrapHandler = (e) => this._trapFocus(e);
            document.addEventListener('keydown', this._focusTrapHandler);
        }

        /**
         * Close panel
         * BULLETPROOF FIX: Auto-apply filters if instant_apply is false and selections changed
         */
        _closePanel() {
            if (!this.panel) return;

            // BULLETPROOF FIX: Auto-apply if filters changed and instant_apply is off
            if (!this.settings.instant_apply) {
                const currentSelections = this._getSelectedFilters();
                const urlFilters = this._getFiltersFromURL();

                const hasChanges = JSON.stringify(currentSelections) !== JSON.stringify(urlFilters);

                if (hasChanges) {
                    this._applyFilters();
                    // Don't close immediately - let applyFilters handle closing
                    return;
                }
            }

            if (this._focusTrapHandler) {
                document.removeEventListener('keydown', this._focusTrapHandler);
                this._focusTrapHandler = null;
            }

            // Remove open class to trigger animation
            this.panel.classList.remove('open');
            if (this.overlay) {
                this.overlay.classList.remove('visible');
            }

            // Hide after animation
            setTimeout(() => {
                this.panel.style.display = 'none';
                if (this.overlay) {
                    this.overlay.style.display = 'none';
                }
            }, this.settings.animation_speed || 300);

            // Update button state
            this.toggle.setAttribute('aria-expanded', 'false');

            // Restore body scroll
            document.body.style.overflow = '';

            // Focus back to toggle button
            this.toggle.focus();
        }

        /**
         * Get filters from URL for comparison
         */
        _getFiltersFromURL() {
            const params = new URLSearchParams(window.location.search);
            const filters = {};

            params.forEach((value, key) => {
                if (key.startsWith('filter_')) {
                    const sourceName = key.replace('filter_', '');
                    filters[sourceName] = value.split(',');
                }
            });

            return filters;
        }

        /**
         * Check if panel is open
         */
        _isPanelOpen() {
            return this.panel && this.panel.classList.contains('open');
        }

        /**
         * Toggle filter group accordion
         */
        _toggleGroup(e) {
            const toggle = e.currentTarget;
            const wrapper = toggle.nextElementSibling;
            if (!wrapper) return;

            const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
            toggle.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
            toggle.classList.toggle('expanded');

            if (isExpanded) {
                this._collapseGroup(wrapper);
            } else {
                this._expandGroup(wrapper);
            }
        }

        /**
         * Expand a filter group with ease-out (starts fast, decelerates naturally).
         */
        _expandGroup(wrapper) {
            // Kill any in-flight transition so rapid clicks don't stack
            wrapper.style.transition = 'none';

            // Remove closed state, measure target height
            wrapper.classList.remove('collapsed');
            wrapper.style.height = 'auto';
            const targetHeight = wrapper.scrollHeight;

            // Snap back to zero so the animation has a clear start
            wrapper.style.height = '0px';
            wrapper.style.opacity = '0';
            wrapper.style.overflow = 'hidden';

            // Force reflow — browser must register the start values before we transition
            // eslint-disable-next-line no-unused-expressions
            wrapper.offsetHeight;

            // Ease-out: rushes open, settles gently at the end
            wrapper.style.transition = 'height 0.26s cubic-bezier(0.0, 0, 0.2, 1), opacity 0.2s ease-out';
            wrapper.style.height = targetHeight + 'px';
            wrapper.style.opacity = '1';

            const onEnd = (ev) => {
                if (ev.propertyName !== 'height') return;
                // Let height be fluid so dynamic content (filter counts) can change it
                wrapper.style.height   = 'auto';
                wrapper.style.overflow = '';
                wrapper.style.transition = '';
                wrapper.removeEventListener('transitionend', onEnd);
            };
            wrapper.addEventListener('transitionend', onEnd);
        }

        /**
         * Collapse a filter group with ease-in (accelerates, ends sharp — no sluggish tail).
         */
        _collapseGroup(wrapper) {
            // Kill any in-flight transition
            wrapper.style.transition = 'none';

            // Lock to current measured height so the browser has an explicit from-value
            wrapper.style.height   = wrapper.scrollHeight + 'px';
            wrapper.style.overflow = 'hidden';

            // Force reflow
            // eslint-disable-next-line no-unused-expressions
            wrapper.offsetHeight;

            // Ease-in: accelerates into the close — ends fast, zero lingering tail
            wrapper.style.transition = 'height 0.2s cubic-bezier(0.4, 0, 1, 1), opacity 0.12s ease-in';
            wrapper.style.height  = '0px';
            wrapper.style.opacity = '0';

            const onEnd = (ev) => {
                if (ev.propertyName !== 'height') return;
                // Hand off to CSS class; clear all inline overrides
                wrapper.classList.add('collapsed');
                wrapper.style.height     = '';
                wrapper.style.overflow   = '';
                wrapper.style.opacity    = '';
                wrapper.style.transition = '';
                wrapper.removeEventListener('transitionend', onEnd);
            };
            wrapper.addEventListener('transitionend', onEnd);
        }

        /**
         * Search filters
         */
        _searchFilters(query) {
            const lowerQuery = query.toLowerCase();
            const filterOptions = this.wrapper.querySelectorAll('.filter-option');

            filterOptions.forEach(option => {
                const label = option.querySelector('.filter-label').textContent.toLowerCase();
                const shouldShow = label.includes(lowerQuery);
                option.style.display = shouldShow ? '' : 'none';
            });
        }

        /**
         * Get selected filter values grouped by source_name
         */
        _getSelectedFilters() {
            const selected = {};

            this.filterCheckboxes.forEach(checkbox => {
                if (checkbox.checked) {
                    const value = checkbox.getAttribute('data-value');

                    // Get the filter group to find source_name
                    const filterGroup = checkbox.closest('.filter-group');
                    if (!filterGroup) return;

                    const sourceName = filterGroup.getAttribute('data-source-name');
                    if (!sourceName) return;

                    if (!selected[sourceName]) {
                        selected[sourceName] = [];
                    }
                    selected[sourceName].push(value);
                }
            });

            return selected;
        }

        /**
         * Apply filters - Use AJAX like regular filters widget (no page reload!)
         */
        async _applyFilters() {
            const selected = this._getSelectedFilters();

            // Update URL without reload using History API
            this._updateURL(selected);

            // Show loading state on apply button
            if (this.applyBtn) {
                this.applyBtn.disabled = true;
                this.applyBtn.textContent = 'Applying...';
            }

            try {
                // Get context (category, taxonomy, etc)
                const context = this._getContext();
                const widgetSettings = this._getProductGridSettings();
                const urlParams = new URLSearchParams(window.location.search);
                const currentOrderby = urlParams.get('orderby');
                const currentOrder = urlParams.get('order');
                const orderby = currentOrderby || widgetSettings.orderby || storedashFiltersData.orderby || 'menu_order';
                const order = currentOrder || widgetSettings.order || storedashFiltersData.order || 'ASC';

                // Make AJAX request to filter endpoint
                const response = await fetch(storedashFiltersData.restUrl + 'storedash/v1/ajax-filter', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': storedashFiltersData.nonce || '',
                    },
                    body: JSON.stringify({
                        filters: selected,
                        context: context,
                        paged: 1,
                        per_page: storedashFiltersData.perPage || 12,
                        orderby: orderby,
                        order: order,
                        widget_settings: widgetSettings,
                        render: 'html',
                    })
                });

                if (!response.ok) {
                    throw new Error('Filter request failed');
                }

                const data = await response.json();

                if (data.success) {
                    // Dispatch the SAME event name as regular filters widget
                    const eventData = {
                        filters: selected,
                        context: context,
                        totalProducts: data.total_products,
                        productIds: data.product_ids,
                        products_html: data.products_html,
                        products: data.products || [],
                        filter_counts: data.filter_counts || {},
                        widget_settings: data.widget_settings || widgetSettings,
                        i18n: data.i18n || {},
                        render_mode: data.render_mode || 'html',
                        cleared: Object.keys(selected).length === 0,
                        totalPages: data.total_pages || 1,
                        currentPage: data.current_page || 1,
                    };

                    const event = new CustomEvent('storedash:filters:changed', {
                        detail: eventData,
                        bubbles: true,
                        cancelable: true
                    });

                    document.dispatchEvent(event);

                    // Fallback: Update product grid HTML directly if no grid widget found
                    const productGrids = document.querySelectorAll('.storedash-products-wrapper, .storedash-product-grid, .elementor-widget-storedash-product-grid');

                    if (productGrids.length === 0) {
                        this._updateProductGridFallback(
                            data.products_html,
                            data.products || [],
                            data.widget_settings || widgetSettings,
                            data.i18n || {}
                        );
                    }

                    // Close panel after successful filter
                    setTimeout(() => {
                        this._closePanel();
                    }, 300);
                }

            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.warn('Storedash: Filter request failed', error.message);
                }
            } finally {
                // Restore button state
                if (this.applyBtn) {
                    this.applyBtn.disabled = false;
                    this.applyBtn.textContent = 'Apply Filters';
                }
            }
        }

        /**
         * Get current page context (category, taxonomy, etc)
         */
        _getContext() {
            const body = document.body;
            const context = {
                is_shop: body.classList.contains('post-type-archive-product') || body.classList.contains('woocommerce-shop'),
                is_category: body.classList.contains('tax-product_cat'),
                is_taxonomy: body.classList.contains('tax-product_tag') || body.classList.contains('woocommerce-taxonomy'),
                category_id: 0,
                taxonomy: '',
                term_id: 0,
            };

            // Extract category ID from body classes
            if (context.is_category) {
                const classes = body.className.split(' ');
                for (let className of classes) {
                    if (className.startsWith('term-')) {
                        const termId = className.replace('term-', '');
                        if (!isNaN(termId)) {
                            context.category_id = parseInt(termId);
                            context.term_id = parseInt(termId);
                            context.taxonomy = 'product_cat';
                            break;
                        }
                    }
                }
            }

            // Extract custom taxonomy
            if (context.is_taxonomy && !context.is_category) {
                const classes = body.className.split(' ');
                for (let className of classes) {
                    if (className.startsWith('term-')) {
                        const termId = className.replace('term-', '');
                        if (!isNaN(termId)) {
                            context.term_id = parseInt(termId);
                        }
                    }
                    if (className.startsWith('tax-')) {
                        context.taxonomy = className.replace('tax-', '');
                    }
                }
            }

            return context;
        }

        /**
         * Retrieve widget settings from the first products wrapper.
         */
        _getProductGridSettings() {
            const productsWrapper = document.querySelector('.storedash-products-wrapper[data-settings]');
            if (productsWrapper && productsWrapper.dataset.settings) {
                try {
                    return JSON.parse(productsWrapper.dataset.settings);
                } catch (e) {
                    return {};
                }
            }

            return {};
        }

        /**
         * Update URL without page reload
         */
        _updateURL(selected) {
            const params = new URLSearchParams(window.location.search);

            // Remove all existing filter params
            const keysToRemove = [];
            params.forEach((_, key) => {
                if (key.startsWith('filter_')) {
                    keysToRemove.push(key);
                }
            });
            keysToRemove.forEach(key => params.delete(key));

            // Remove pagination params (filters reset pagination to page 1)
            params.delete('paged');
            params.delete('page');

            // Add new filter values
            Object.keys(selected).forEach(sourceName => {
                if (selected[sourceName].length > 0) {
                    params.set('filter_' + sourceName, selected[sourceName].join(','));
                }
            });

            // Build new URL
            let newUrl = window.location.pathname;
            const queryString = params.toString();

            if (queryString) {
                newUrl += '?' + queryString;
            }

            // Update URL without reload using History API
            history.pushState({ filters: selected }, '', newUrl);
        }

        /**
         * Reset filters - Clear all selections and apply empty filter
         */
        _resetFilters() {
            // Uncheck all checkboxes
            this.filterCheckboxes.forEach(checkbox => {
                checkbox.checked = false;
            });

            // Clear search if it exists
            if (this.searchInput) {
                this.searchInput.value = '';
                // Show all options again
                const filterOptions = this.wrapper.querySelectorAll('.filter-option');
                filterOptions.forEach(option => {
                    option.style.display = '';
                });
            }

            // Apply empty filters (shows all products)
            this._applyFilters();
        }

        /**
         * Sync checkboxes with URL parameters
         * Checks boxes that match filter values in the URL
         */
        _syncWithURL() {
            // Parse URL parameters
            const params = new URLSearchParams(window.location.search);
            const activeFilters = {};

            // Extract all filter params from URL
            params.forEach((value, key) => {
                if (key.startsWith('filter_')) {
                    const sourceName = key.replace('filter_', '');
                    activeFilters[sourceName] = value.split(',');
                }
            });

            // Check boxes that match URL params
            this.filterCheckboxes.forEach(checkbox => {
                const filterGroup = checkbox.closest('.filter-group');
                if (!filterGroup) return;

                const sourceName = filterGroup.getAttribute('data-source-name');
                const value = checkbox.getAttribute('data-value');

                // Check if this filter value is in the URL
                if (activeFilters[sourceName] && activeFilters[sourceName].includes(value)) {
                    checkbox.checked = true;
                }
            });
        }

        /**
         * Update product grid HTML directly (fallback for standard WooCommerce)
         */
        _updateProductGridFallback(productsHTML, productsData = [], widgetSettings = {}, i18n = {}) {
            const productsContainer = document.querySelector('.products, .woocommerce-loop-product, ul.products, .storedash-products-wrapper');

            if (productsContainer) {
                // Fade out, update, fade in for smooth transition
                productsContainer.style.transition = 'opacity 0.2s';
                productsContainer.style.opacity = '0.3';

                setTimeout(() => {
                    let finalHTML = productsHTML;

                    if (!finalHTML && typeof window.StoredashProductRenderer !== 'undefined') {
                        finalHTML = window.StoredashProductRenderer.renderProducts(productsData || [], widgetSettings || {}, i18n || {});
                    }

                    if (!finalHTML) {
                        finalHTML = '';
                    }

                    productsContainer.innerHTML = finalHTML;
                    productsContainer.style.opacity = '1';

                    // Scroll to top of products
                    const offset = productsContainer.getBoundingClientRect().top + window.pageYOffset - 100;
                    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                    window.scrollTo({
                        top: offset,
                        behavior: prefersReducedMotion ? 'auto' : 'smooth'
                    });
                }, 200);
            }
        }
    }

    /**
     * Initialize all slide-out filters widgets on page
     */
    function initSlideOutFilters() {
        const wrappers = document.querySelectorAll('.storedash-slide-out-filters-wrapper');

        if (wrappers.length === 0) {
            return;
        }

        wrappers.forEach((wrapper) => {
            try {
                new SlideOutFilters(wrapper);
            } catch (error) {
                console.warn('Storedash: Slide-out filters init failed', error.message);
            }
        });
    }

    /**
     * Initialize on DOM ready
     */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initSlideOutFilters);
    } else {
        initSlideOutFilters();
    }

    /**
     * Re-initialize for AJAX-loaded content
     */
    if (window.addEventListener) {
        document.addEventListener('elementor/popup/show', initSlideOutFilters);
    }

    /**
     * Elementor editor integration
     * Re-initialize widgets when editor updates the page
     */
    if (typeof elementor !== 'undefined' && elementor.on) {
        elementor.on('preview:updated', function() {
            initSlideOutFilters();
        });

        elementor.on('preview:portableContentToggled', function() {
            initSlideOutFilters();
        });
    }

    /**
     * MutationObserver for dynamic content changes
     * Re-initialize if new widgets are added to the page
     */
    // PERF FIX: Scope observer to .elementor or #content instead of document.body
    if (typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver(function(mutations) {
            // Check if new slide-out-filters widgets were added
            const hasNewWidgets = mutations.some(mutation => {
                return Array.from(mutation.addedNodes).some(node => {
                    return node.classList && node.classList.contains('storedash-slide-out-filters-wrapper');
                });
            });

            if (hasNewWidgets) {
                setTimeout(initSlideOutFilters, 100);
            }
        });

        const observeTarget = document.querySelector('.elementor') || document.getElementById('content') || document.body;
        observer.observe(observeTarget, {
            childList: true,
            subtree: true
        });
    }

    // Add focus trap methods to prototype
    SlideOutFilters.prototype._getFocusableElements = function() {
        if (!this.panel) return [];
        return Array.from(this.panel.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )).filter(el => !el.disabled && el.offsetParent !== null);
    };

    SlideOutFilters.prototype._trapFocus = function(e) {
        if (e.key !== 'Tab') return;
        const focusable = this._getFocusableElements();
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };

    // Expose to global scope for manual initialization if needed
    window.Storedash = window.Storedash || {};
    window.Storedash.SlideOutFilters = SlideOutFilters;
    window.StoredashSlideOutFilters = SlideOutFilters; // backward compat
})();
